import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getAegArtikelen } from "@/lib/aeg-artikelen.functions";
import { Header } from "@/components/site/Header";
import aegLogo from "@/assets/aeg.png.asset.json";
import { Footer } from "@/components/site/Footer";
import { KlantGegevensPoort } from "@/components/site/KlantGegevensPoort";
import { leesKlantgegevens, type Klantgegevens } from "@/lib/klantgegevens";
import { toast } from "sonner";
import {
  Calculator,
  ArrowRight,
  AlertTriangle,
  ShieldCheck,
  ChevronDown,
  Cpu,
  ListChecks,
  Layers,
  Zap,
  Share2,
  Check,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { GroepenkastCanvas } from "@/components/site/GroepenkastCanvas";
import {
  GroepenkastBasisConfig,
  type BasisConfigResult,
} from "@/components/site/GroepenkastBasisConfig";
import {
  buildModuleBlocks,
  describeConfiguration,
} from "@/lib/groepenkast-modules";


const SITE_URL = "https://elvion-group.nl";

const GROUP_KEYS = [
  "group",
  "kitchen3",
  "ev3",
  "ev22",
  "extra",
] as const;

const searchSchema = z.object({
  p: fallback(z.union([z.literal(1), z.literal(3)]), 3).default(3),
  a: fallback(z.number().int().min(6).max(160), 25).default(25),
  spd: fallback(z.union([z.literal(0), z.literal(1)]), 1).default(1),
  r: fallback(z.number().int().min(0).max(50), 20).default(20),
  g: fallback(z.string(), "").default(""),
  l: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/groepenkast-configurator")({
  validateSearch: zodValidator(searchSchema),
  head: () => {
    const url = `${SITE_URL}/groepenkast-configurator`;
    const title =
      "Groepenkast Configurator — stel je NEN 1010 kast samen | Elvion Group Nederland";
    const description =
      "Stel in een paar klikken je groepenkast samen: bepaal het aantal groepen, de juiste modulebreedte, aardlekautomaten en eventueel aparte groepen voor laadpaal, warmtepomp en kookplaat — volledig NEN 1010 conform.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: GroepenkastConfiguratorPage,
});

function encodeGroups(counts: Record<string, number>): string {
  return GROUP_KEYS
    .filter((k) => (counts[k] || 0) > 0)
    .map((k) => `${k}:${counts[k]}`)
    .join(",");
}

function decodeGroups(
  raw: string,
  fallbackCounts: Record<(typeof GROUP_KEYS)[number], number>,
): Record<(typeof GROUP_KEYS)[number], number> {
  if (!raw) return fallbackCounts;
  const out: Record<string, number> = Object.fromEntries(
    GROUP_KEYS.map((k) => [k, 0]),
  );
  for (const part of raw.split(",")) {
    const [k, v] = part.split(":");
    if ((GROUP_KEYS as readonly string[]).includes(k)) {
      const n = Math.max(0, Math.min(99, parseInt(v ?? "0", 10) || 0));
      out[k] = n;
    }
  }
  return out as Record<(typeof GROUP_KEYS)[number], number>;
}

/** Groepslabels ↔ deel-link, formaat: light#0=Woonkamer|socket#1=Zolder */
function encodeLabels(labels: Record<string, string>): string {
  return Object.entries(labels)
    .filter(([, v]) => v.trim())
    .map(([k, v]) => `${k}=${encodeURIComponent(v.trim().slice(0, 24))}`)
    .join("|");
}

function decodeLabels(raw: string): Record<string, string> {
  if (!raw) return {};
  const out: Record<string, string> = {};
  for (const part of raw.split("|")) {
    const idx = part.indexOf("=");
    if (idx <= 0) continue;
    const key = part.slice(0, idx);
    try {
      out[key] = decodeURIComponent(part.slice(idx + 1)).slice(0, 24);
    } catch {
      // ongeldige waarde overslaan
    }
  }
  return out;
}



// Module-breedtes (1 module = 18 mm DIN-rail)
const STANDARD_CABINETS = [24, 48, 72, 96];

const FAQS = [
  {
    q: "Hoe bepaalt deze configurator het aantal modules?",
    a: "De meeste groepen zijn 1 module breed (16 A, 1-fase). De kookgroep 1-fase neemt 2 modules in, een kookgroep of laadgroep 3-fase 4 modules. De hoofdschakelaar neemt 2 modules (1-fase) of 4 modules (3-fase) in, elke aardlekschakelaar 2 modules (1-fase) of 4 modules (3-fase) en de overspanningsbeveiliging type 2 neemt 2 of 4 modules in. Een kast is 2 rails × 12 modules = 24 modules; bij meer dan 24 modules voegen we een extra kast toe. We rekenen daarnaast standaard 20% reserve.",
  },
  {
    q: "Welke aardlekautomaat heb ik per groep nodig?",
    a: "Voor lichtgroepen en stopcontacten in droge ruimtes volstaat een ALB type A (16 A, B-karakteristiek). Voor natte ruimtes (badkamer, buiten) is type A verplicht. Voor laadpalen en gelijkstroomverbruikers (zonnepanelen, omvormers) schrijft NEN 1010 een ALB type B voor, omdat type A geen DC-lekstroom detecteert.",
  },
  {
    q: "Moet ik overspanningsbeveiliging (SPD) opnemen?",
    a: "Sinds NEN 1010:2020 (clausule 443) is overspanningsbeveiliging type 2 verplicht in nieuwe installaties bij woningen en kleinere bedrijfspanden in heel Nederland. Een SPD beschermt je installatie en gevoelige elektronica tegen overspanning door blikseminslag of schakelpieken in het net.",
  },
  {
    q: "Wanneer kies ik een 1-fase en wanneer een 3-fase groepenkast?",
    a: "1-fase kasten (230 V) volstaan voor woningen tot circa 8 groepen zonder zware verbruikers. Heb je inductie, warmtepomp, laadpaal, zonnepanelen of een werkplaats? Kies dan 3-fase (400 V) — die verdeelt het vermogen over drie fasen, voorkomt scheve belasting en kan tot 17,3 kW (3×25 A) leveren.",
  },
  {
    q: "Hoeveel reserve moet ik aanhouden in mijn groepenkast?",
    a: "We adviseren minimaal 20% vrije modules voor toekomstige uitbreiding (laadpaal, thuisbatterij, warmtepomp, extra kookgroep). Een te volle kast vereist later een complete vervanging in plaats van een eenvoudige uitbreiding, en dat kost ongeveer drie keer zoveel.",
  },
  {
    q: "Wat is een kookgroep in deze configurator?",
    a: "Een standaard kookgroep is 16 A, 1-fase en neemt 2 modules in beslag. Heb je een zwaardere 3-fase inductiekookplaat? Kies dan 'Kookgroep 3-fase' (16 A, 4 modules). De kookgroep is een aparte groep voor het fornuis / inductiekookplaat.",
  },
  {
    q: "Heeft een laadpaal een aparte groep nodig?",
    a: "Ja — een laadpaal vereist altijd een eigen groep met een aardlekautomaat type B. In deze configurator plaatsen we een 1-fase 16 A laadpaal als 1 module. Voor 3-fase 11 kW of 22 kW laden kies je de bijbehorende 3-fase laadgroep (4 modules).",
  },
  {
    q: "Moet ik mijn meterkast verzwaren bij vervanging?",
    a: "Niet automatisch. De groepenkast vervangen kan op je bestaande aansluiting (bijv. 1×25 A of 3×25 A). Pas als de nieuwe configuratie meer vermogen vraagt dan je hoofdzekering aankan, vraag je een verzwaring aan bij je netbeheerder — reken op 2–6 weken doorlooptijd. In de offerte nemen we deze kosten mee als ze van toepassing zijn.",
  },
  {
    q: "Wat kost een nieuwe groepenkast op basis van deze configuratie?",
    a: "Elke situatie is anders: de totaalprijs hangt af van je gekozen kast, groepen, aardlekschakelaars en eventuele extra's zoals een overspanningsbeveiliging of laadpaalgroep. Vraag via de knop hieronder een vrijblijvende offerte aan op basis van de gegenereerde stuklijst, dan ontvang je binnen 1 werkdag een prijsopgave inclusief materialen, installatie en arbeid.",
  },
  {
    q: "Mag ik de configuratie zelf installeren?",
    a: "Nee. Volgens de Wet op de Installaties in Woningen (WIW) en NEN 1010 mag uitsluitend een erkend installateur vaste elektrische installaties plaatsen of wijzigen. Zelf installeren is niet verzekerd, levert problemen op bij verkoop van het pand en kan levensgevaarlijk zijn. Bestel de configuratie via ons en wij regelen plaatsing.",
  },
];

type GroupKey = "group" | "kitchen3" | "ev3" | "ev22" | "extra";

interface GroupSpec {
  key: GroupKey;
  label: string;
  hint: string;
  modules: number; // DIN modules occupied by ALB
  type: "A" | "B";
  amps: number;
  phases: 1 | 3;
  kw: number;
}

const DEFAULT_COUNTS: Record<GroupKey, number> = {
  group: 8,
  kitchen3: 0,
  ev3: 0,
  ev22: 0,
  extra: 0,
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildGroepenkastPdfHtml(input: {
  projectNumber: string;
  customerName: string;
  projectDate: string;
  phases: 1 | 3;
  mainAmps: number;
  includeSpd: boolean;
  reservePct: number;
  items: Array<{
    label: string;
    type: "A" | "B";
    amps: number;
    qty: number;
    modules: number;
  }>;
  mainSwitchModules: number;
  spdModules: number;
  reserveModules: number;
  cabinet: number;
  installedKw: number;
  expectedKw: number;
  maxKw: number;
  materialCost: number;
  groupLabels?: string[];
}): string {
  const dateLabel = input.projectDate
    ? new Date(input.projectDate).toLocaleDateString("nl-NL")
    : "";
  const rows = input.items
    .map(
      (g) => `
      <tr>
        <td>${escapeHtml(g.label)} <span class="tag tag-${g.type}">ALB ${g.type} · ${g.amps} A</span></td>
        <td class="num">${g.qty}×</td>
        <td class="num">${g.modules}</td>
      </tr>`,
    )
    .join("");
  const cost = Math.round(input.materialCost).toLocaleString("nl-NL");
  return `<!doctype html><html lang="nl"><head><meta charset="utf-8" />
<title>Bestellijst AEG groepenkast ${escapeHtml(input.projectNumber || "")} — Elvion Group Nederland</title>
<style>
  @page { size: A4; margin: 16mm 14mm 18mm; }
  *, *::before, *::after { box-sizing: border-box; }
  html, body { background: #fff; color: #1a2733; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; font-size: 12px; line-height: 1.5; margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { padding: 28px 32px 40px; max-width: 210mm; margin: 0 auto; }
  h1 { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; margin: 0; }
  h2 { font-size: 16px; font-weight: 800; letter-spacing: -0.02em; margin: 0; }
  h3 { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #f08a24; margin: 0 0 8px; }
  .brand { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #f08a24; padding-bottom: 14px; margin-bottom: 22px; }
  .logo-text { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; color: #0b2a4a; }
  .logo-text span { color: #f08a24; }
  .badge { display: inline-block; background: #f08a24; color: #fff; padding: 3px 10px; border-radius: 999px; font-size: 9.5px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin: 16px 0 22px; }
  .card { border: 1px solid #e3e7eb; border-radius: 12px; padding: 14px 16px; }
  .row { display: flex; justify-content: space-between; gap: 12px; padding: 3px 0; font-size: 11.5px; }
  .row .k { color: #5a6b7b; } .row .v { font-weight: 600; text-align: right; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th, td { padding: 8px 10px; border-bottom: 1px solid #e3e7eb; font-size: 11.5px; text-align: left; }
  th { background: #f5f7f9; text-transform: uppercase; font-size: 10px; letter-spacing: .06em; color: #5a6b7b; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  .tag { display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: 4px; font-size: 9.5px; font-weight: 700; }
  .tag-A { background: #f5f7f9; color: #5a6b7b; }
  .tag-B { background: rgba(240,138,36,.12); color: #f08a24; }
  .total { margin-top: 14px; padding: 12px 14px; border-radius: 12px; background: rgba(240,138,36,.08); border: 1px solid rgba(240,138,36,.4); }
  .total .lbl { font-size: 10.5px; text-transform: uppercase; letter-spacing: .08em; color: #f08a24; font-weight: 700; }
  .total .val { font-size: 22px; font-weight: 800; }
  .foot { margin-top: 24px; font-size: 10.5px; color: #5a6b7b; }
  .noprint { margin-bottom: 14px; }
  .noprint button { background: #f08a24; color: #fff; border: 0; padding: 8px 14px; border-radius: 8px; font-weight: 700; cursor: pointer; }
  @media print { .noprint { display: none; } }
</style></head><body>
<div class="noprint"><button onclick="window.print()">Print / opslaan als PDF</button></div>
<div class="brand">
  <div class="logo-text">Elvion <span>Group</span></div>
  <div style="text-align:right; font-size:10.5px; color:#5a6b7b;">
    <div><span class="badge">Bestellijst AEG groepenkast</span></div>
    <div style="margin-top:4px;">elvion-group.nl</div>
  </div>
</div>
<h1>Bestellijst AEG groepenkast</h1>
<div class="grid">
  <div class="card">
    <h3>Projectgegevens</h3>
    <div class="row"><span class="k">Projectnummer</span><span class="v">${escapeHtml(input.projectNumber || "—")}</span></div>
    <div class="row"><span class="k">Klant</span><span class="v">${escapeHtml(input.customerName || "—")}</span></div>
    <div class="row"><span class="k">Datum</span><span class="v">${escapeHtml(dateLabel)}</span></div>
  </div>
  <div class="card">
    <h3>Installatie</h3>
    <div class="row"><span class="k">Aansluiting</span><span class="v">${input.phases}×${input.mainAmps} A (${input.phases === 1 ? "230 V" : "400 V"})</span></div>
    <div class="row"><span class="k">SPD type 2</span><span class="v">${input.includeSpd ? "Ja" : "Nee"}</span></div>
    <div class="row"><span class="k">Reserve</span><span class="v">${input.reservePct}%</span></div>
    <div class="row"><span class="k">Aanbevolen kast</span><span class="v">${input.cabinet} modules</span></div>
    <div class="row"><span class="k">Geïnstalleerd / verwacht</span><span class="v">${input.installedKw.toFixed(1)} kW / ${input.expectedKw.toFixed(1)} kW (max ${input.maxKw.toFixed(1)} kW)</span></div>
  </div>
</div>
<h2>Stuklijst AEG (NEN 1010:2020)</h2>
<table>
  <thead><tr><th>Onderdeel</th><th class="num">Aantal</th><th class="num">Modules</th></tr></thead>
  <tbody>
    <tr><td><strong>Hoofdschakelaar ${input.phases === 1 ? "2P" : "4P"} ${input.mainAmps} A</strong></td><td class="num">1×</td><td class="num">${input.mainSwitchModules}</td></tr>
    ${input.includeSpd ? `<tr><td><strong>Overspanningsbeveiliging type 2 (${input.phases === 1 ? "1+N" : "3+N"})</strong></td><td class="num">1×</td><td class="num">${input.spdModules}</td></tr>` : ""}
    ${rows}
    <tr style="background:rgba(240,138,36,.05);"><td><strong>Reserve (${input.reservePct}%) + behuizing ${input.cabinet} mod</strong></td><td class="num">—</td><td class="num"><strong>${input.reserveModules}</strong></td></tr>
  </tbody>
</table>
<div class="total">
  <div class="lbl">Materiaaloverzicht</div>
  <div class="val">Prijs op aanvraag</div>
  <div style="font-size:10.5px; color:#5a6b7b; margin-top:4px;">Offerte op basis van deze stuklijst volgt binnen 1 werkdag. Definitieve keuze (merk, karakteristiek, kortsluitvastheid) door erkend installateur.</div>
</div>
${
  input.groupLabels && input.groupLabels.length
    ? `<h2 style="margin-top:22px;">Groepenkaart</h2><table><thead><tr><th>Nr.</th><th>Omschrijving</th></tr></thead><tbody>${input.groupLabels
        .map((l, i) => `<tr><td class="num">${i + 1}</td><td>${escapeHtml(l)}</td></tr>`)
        .join("")}</tbody></table>`
    : ""
}
<p class="foot">Gegenereerd door de Elvion Group Nederland Groepenkast Configurator — conform NEN 1010:2020.</p>
</body></html>`;
}


function GroepenkastConfiguratorPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  // Eerst de klantgegevens die de serviceplanner nodig heeft, dan pas de
  // configurator.
  const [klant, setKlant] = useState<Klantgegevens | null>(null);
  const [klantGeladen, setKlantGeladen] = useState(false);
  useEffect(() => {
    setKlant(leesKlantgegevens());
    setKlantGeladen(true);
  }, []);

  const initialCounts = useMemo(
    () => decodeGroups(search.g, DEFAULT_COUNTS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [phases, setPhases] = useState<1 | 3>(search.p);
  const [mainAmps, setMainAmps] = useState<number>(search.a);
  const [counts, setCounts] = useState<Record<GroupKey, number>>(initialCounts);
  const [includeSpd, setIncludeSpd] = useState<boolean>(search.spd === 1);
  const [reservePct, setReservePct] = useState<number>(search.r);
  const [labels, setLabels] = useState<Record<string, string>>(() =>
    decodeLabels(search.l),
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [overrideAllowed, setOverrideAllowed] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<{
    key: GroupKey;
    newKw: number;
    maxKw: number;
  } | null>(null);

  // Projectgegevens
  const PROJECT_STORAGE_KEY = "groepenkast-configurator:project:v1";
  type StoredProject = {
    projectNumber?: string;
    customerName?: string;
    projectDate?: string;
  };
  const readStoredProject = (): StoredProject => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(PROJECT_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as StoredProject) : {};
    } catch {
      return {};
    }
  };
  const storedProject =
    typeof window !== "undefined" ? readStoredProject() : {};
  const [projectNumber, setProjectNumber] = useState<string>(
    storedProject.projectNumber ?? "",
  );
  const [customerName, setCustomerName] = useState<string>(
    storedProject.customerName ?? "",
  );
  const [projectDate, setProjectDate] = useState<string>(
    storedProject.projectDate ?? new Date().toISOString().slice(0, 10),
  );
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [currentConfigId, setCurrentConfigId] = useState<string | null>(null);
  const [savingRemote, setSavingRemote] = useState(false);
  const [savedConfigs, setSavedConfigs] = useState<
    Array<{
      id: string;
      project_number: string | null;
      customer_name: string | null;
      project_date: string;
      updated_at: string;
    }>
  >([]);

  // Auto-save project meta to localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handle = window.setTimeout(() => {
      try {
        window.localStorage.setItem(
          PROJECT_STORAGE_KEY,
          JSON.stringify({ projectNumber, customerName, projectDate }),
        );
        setLastSavedAt(new Date());
      } catch {
        // ignore
      }
    }, 400);
    return () => window.clearTimeout(handle);
  }, [projectNumber, customerName, projectDate]);

  // Track auth state
  useEffect(() => {
    let active = true;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      setUserId(data.session?.user?.id ?? null);
      const sub = supabase.auth.onAuthStateChange((_e, session) => {
        setUserId(session?.user?.id ?? null);
      });
      (window as unknown as { __groepAuthSub?: { unsubscribe(): void } }).__groepAuthSub =
        sub.data.subscription;
    })();
    return () => {
      active = false;
      try {
        (window as unknown as { __groepAuthSub?: { unsubscribe(): void } }).__groepAuthSub?.unsubscribe?.();
      } catch {
        // ignore
      }
    };
  }, []);

  const refreshSavedConfigs = async () => {
    if (!userId) {
      setSavedConfigs([]);
      return;
    }
    const { supabase } = await import("@/integrations/supabase/client");
    const { data, error } = await supabase
      .from("groepenkast_configurations")
      .select("id, project_number, customer_name, project_date, updated_at")
      .order("updated_at", { ascending: false })
      .limit(50);
    if (!error && data) setSavedConfigs(data as typeof savedConfigs);
  };

  useEffect(() => {
    refreshSavedConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Cross-device autosave: hydrate from account on sign-in, then push changes.
  const autosaveHydrated = useRef(false);
  const autosaveHydratedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) {
      autosaveHydrated.current = false;
      autosaveHydratedFor.current = null;
      return;
    }
    if (autosaveHydratedFor.current === userId) return;
    let cancelled = false;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase
        .from("groepenkast_autosave")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        // Only apply if this device has no explicit shared link / loaded config.
        const hasUrlState =
          typeof window !== "undefined" &&
          window.location.search.includes("g=");
        if (!hasUrlState && !currentConfigId) {
          setProjectNumber(data.project_number ?? "");
          setCustomerName(data.customer_name ?? "");
          setProjectDate(
            data.project_date ?? new Date().toISOString().slice(0, 10),
          );
          setPhases((data.phases === 1 ? 1 : 3) as 1 | 3);
          setMainAmps(data.main_amps ?? 25);
          setIncludeSpd(!!data.include_spd);
          setReservePct(data.reserve_pct ?? 20);
          const loaded = (data.counts ?? {}) as Record<string, number>;
          const merged = { ...DEFAULT_COUNTS } as Record<GroupKey, number>;
          for (const k of GROUP_KEYS)
            merged[k] = Math.max(0, Number(loaded[k]) || 0);
          setCounts(merged);
          toast.success("Configuratie hersteld van je account", {
            duration: 2500,
          });
        }
      }
      autosaveHydratedFor.current = userId;
      // Allow auto-push on subsequent state changes.
      autosaveHydrated.current = true;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Debounced push to account whenever state changes.
  useEffect(() => {
    if (!userId || !autosaveHydrated.current) return;
    const handle = window.setTimeout(async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        await supabase.from("groepenkast_autosave").upsert(
          {
            user_id: userId,
            project_number: projectNumber || null,
            customer_name: customerName || null,
            project_date: projectDate,
            phases,
            main_amps: mainAmps,
            include_spd: includeSpd,
            reserve_pct: reservePct,
            counts: counts as unknown as Record<string, number>,
          },
          { onConflict: "user_id" },
        );
      } catch {
        // silent — local state is still saved in localStorage + URL
      }
    }, 800);
    return () => window.clearTimeout(handle);
  }, [
    userId,
    projectNumber,
    customerName,
    projectDate,
    phases,
    mainAmps,
    includeSpd,
    reservePct,
    counts,
  ]);

  // Sync state → URL search params (replace, no history spam, retain scroll)
  useEffect(() => {
    const g = encodeGroups(counts);
    navigate({
      replace: true,
      resetScroll: false,
      search: {
        p: phases,
        a: mainAmps,
        spd: (includeSpd ? 1 : 0) as 0 | 1,
        r: reservePct,
        g,
        l: encodeLabels(labels),
      },
    });
  }, [phases, mainAmps, includeSpd, reservePct, counts, labels, navigate]);

  const applyBasisConfig = (cfg: BasisConfigResult) => {
    setPhases(cfg.phases);
    setMainAmps(cfg.mainAmps);
    setIncludeSpd(cfg.includeSpd);
    setCounts(cfg.counts as Record<GroupKey, number>);
    setLabels({});
    setSelectedBlockId(null);
    setOverrideAllowed(true);
    toast.success("Basisconfiguratie toegepast");
  };


  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      const nav = typeof navigator !== "undefined" ? (navigator as Navigator & { share?: (d: ShareData) => Promise<void> }) : undefined;
      if (nav?.share) {
        await nav.share({
          title: "Mijn groepenkast configuratie",
          text: "Bekijk mijn groepenkast configuratie",
          url,
        });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Deelbare link gekopieerd naar klembord");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kon de link niet kopiëren");
    }
  };



  const fetchAegArtikelen = useServerFn(getAegArtikelen);
  const aegQuery = useQuery({
    queryKey: ["aeg-artikelen"],
    queryFn: () => fetchAegArtikelen(),
    staleTime: 1000 * 60 * 60,
  });
  const aegArtikelen = aegQuery.data?.artikelen ?? [];

  const groups: GroupSpec[] = useMemo(
    () => [
      { key: "group", label: "Groep 16 A 1-fase", hint: "16 A 1-fase, 1 mod", modules: 1, type: "A", amps: 16, phases: 1, kw: 3.7 },
      { key: "kitchen3", label: "Kookgroep 3-fase", hint: "16 A 3-fase, 4 mod", modules: 4, type: "A", amps: 16, phases: 3, kw: 11 },
      { key: "ev3", label: "Laadpaal 3-fase (11 kW)", hint: "16 A + type B, 4 mod", modules: 4, type: "B", amps: 16, phases: 3, kw: 11 },
      { key: "ev22", label: "Laadpaal 3-fase (22 kW)", hint: "32 A + type B, 4 mod", modules: 4, type: "B", amps: 32, phases: 3, kw: 22 },
      { key: "extra", label: "Reservegroep / extra", hint: "16 A 1-fase, 1 mod", modules: 1, type: "A", amps: 16, phases: 1, kw: 3.7 },
    ],
    [],
  );

  const result = useMemo(() => {
    const items = groups
      .map((g) => ({ ...g, qty: counts[g.key] || 0 }))
      .filter((g) => g.qty > 0);

    const groupCount = items.reduce((acc, g) => acc + g.qty, 0);
    const albAModules = items
      .filter((g) => g.type === "A")
      .reduce((acc, g) => acc + g.modules * g.qty, 0);
    const albBModules = items
      .filter((g) => g.type === "B")
      .reduce((acc, g) => acc + g.modules * g.qty, 0);
    const mainSwitchModules = phases === 1 ? 2 : 4;
    const rcdModules = 3 * (phases === 1 ? 2 : 4); // 3 aardlekschakelaars van 2 of 4 modules
    const spdModules = includeSpd ? (phases === 1 ? 2 : 4) : 0;

    const usedModules = albAModules + albBModules + mainSwitchModules + rcdModules + spdModules;
    const reserveModules = Math.ceil((usedModules * reservePct) / 100);
    const totalModules = usedModules + reserveModules;

    const cabinet =
      STANDARD_CABINETS.find((c) => c >= totalModules) ??
      STANDARD_CABINETS[STANDARD_CABINETS.length - 1];

    // Indicatief geïnstalleerd vermogen — som van groepen
    const installedKw = items.reduce((acc, g) => acc + g.kw * g.qty, 0);
    // Diversiteitsfactor: in een woning loopt nooit alles tegelijk op vol vermogen
    const diversityFactor = 0.5;
    const expectedKw = installedKw * diversityFactor;

    const maxKw =
      phases === 1
        ? (230 * mainAmps * 0.95) / 1000
        : (Math.sqrt(3) * 400 * mainAmps * 0.95) / 1000;

    const overload = expectedKw > maxKw;

    // Indicatieve materiaalkosten
    const priceAlbA = 28;
    const priceAlbB = 165;
    const priceMain = phases === 1 ? 35 : 75;
    const priceSpd = phases === 1 ? 95 : 145;
    const priceCabinetPerModule = 9;
    const materialCost =
      albAModules * priceAlbA +
      albBModules * priceAlbB +
      priceMain +
      (includeSpd ? priceSpd : 0) +
      cabinet * priceCabinetPerModule;

    return {
      items,
      groupCount,
      albAModules,
      albBModules,
      mainSwitchModules,
      spdModules,
      usedModules,
      reserveModules,
      totalModules,
      cabinet,
      installedKw,
      expectedKw,
      maxKw,
      overload,
      materialCost,
    };
  }, [groups, counts, phases, mainAmps, includeSpd, reservePct]);

  // Blokken voor de visuele weergave van de kast
  const moduleBlocks = useMemo(
    () =>
      buildModuleBlocks({
        phases,
        mainAmps,
        includeSpd,
        reserveModules: result.reserveModules,
        items: result.items.map((i) => ({
          key: i.key,
          label: i.label,
          modules: i.modules,
          type: i.type,
          amps: i.amps,
          qty: i.qty,
        })),
        labels,
      }),
    [phases, mainAmps, includeSpd, result, labels],
  );

  const configSummary = useMemo(
    () =>
      describeConfiguration({
        phases,
        mainAmps,
        includeSpd,
        cabinet: result.cabinet,
        usedModules: result.usedModules,
        reserveModules: result.reserveModules,
        items: result.items.map((i) => ({
          label: i.label,
          qty: i.qty,
          type: i.type,
          amps: i.amps,
        })),
        labels,
      }),
    [phases, mainAmps, includeSpd, result, labels],
  );

  const selectedBlock = moduleBlocks.find((b) => b.id === selectedBlockId) ?? null;

  // Refs voor het labelveld; we scrollen/focussen niet automatisch na het
  // aantikken van een groep, zodat de configurator op zijn plek blijft staan.
  const labelPanelRef = useRef<HTMLDivElement | null>(null);
  const labelInputRef = useRef<HTMLInputElement | null>(null);

  const requestQuote = () => {
    try {
      window.sessionStorage.setItem(
        "groepenkast-configuratie",
        configSummary,
      );
    } catch {
      // niet beschikbaar in privémodus — dan gaat de klant zonder samenvatting verder
    }
    navigate({ to: "/offerte-aanvragen", search: { product: "groepenkast" } });
  };



  useEffect(() => {
    if (overrideAllowed && !result.overload) setOverrideAllowed(false);
  }, [overrideAllowed, result.overload]);

  const applyDelta = (key: GroupKey, delta: number) =>
    setCounts((c) => ({ ...c, [key]: Math.max(0, (c[key] || 0) + delta) }));

  const setCount = (key: GroupKey, delta: number) => {
    if (delta <= 0) {
      applyDelta(key, delta);
      return;
    }
    const spec = groups.find((g) => g.key === key);
    if (!spec) return;
    const installedKw =
      groups.reduce((acc, g) => acc + g.kw * (counts[g.key] || 0), 0) + spec.kw;
    const newExpected = installedKw * 0.5;
    const maxKw =
      phases === 1
        ? (230 * mainAmps * 0.95) / 1000
        : (Math.sqrt(3) * 400 * mainAmps * 0.95) / 1000;
    if (newExpected > maxKw && !overrideAllowed) {
      setPendingAdd({ key, newKw: newExpected, maxKw });
      return;
    }
    applyDelta(key, delta);
  };

  const saveToAccount = async () => {
    if (!userId) {
      toast.error("Log in om in je account op te slaan");
      return;
    }
    setSavingRemote(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const payload = {
        user_id: userId,
        project_number: projectNumber || null,
        customer_name: customerName || null,
        project_date: projectDate,
        phases,
        main_amps: mainAmps,
        include_spd: includeSpd,
        reserve_pct: reservePct,
        counts: counts as unknown as Record<string, number>,
      };
      if (currentConfigId) {
        const { error } = await supabase
          .from("groepenkast_configurations")
          .update(payload)
          .eq("id", currentConfigId);
        if (error) throw error;
        toast.success("Configuratie bijgewerkt in je account");
      } else {
        const { data, error } = await supabase
          .from("groepenkast_configurations")
          .insert(payload)
          .select("id")
          .single();
        if (error) throw error;
        setCurrentConfigId(data.id);
        toast.success("Configuratie opgeslagen in je account");
      }
      await refreshSavedConfigs();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Opslaan mislukt";
      toast.error(msg);
    } finally {
      setSavingRemote(false);
    }
  };

  const loadFromAccount = async (id: string) => {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data, error } = await supabase
      .from("groepenkast_configurations")
      .select("*")
      .eq("id", id)
      .single();
    if (error || !data) {
      toast.error("Laden mislukt");
      return;
    }
    setCurrentConfigId(data.id);
    setProjectNumber(data.project_number ?? "");
    setCustomerName(data.customer_name ?? "");
    setProjectDate(
      data.project_date ?? new Date().toISOString().slice(0, 10),
    );
    setPhases((data.phases === 1 ? 1 : 3) as 1 | 3);
    setMainAmps(data.main_amps ?? 25);
    setIncludeSpd(!!data.include_spd);
    setReservePct(data.reserve_pct ?? 20);
    const loaded = (data.counts ?? {}) as Record<string, number>;
    const merged = { ...DEFAULT_COUNTS } as Record<GroupKey, number>;
    for (const k of GROUP_KEYS) merged[k] = Math.max(0, Number(loaded[k]) || 0);
    setCounts(merged);
    toast.success("Configuratie geladen");
  };

  const deleteFromAccount = async (id: string) => {
    const { supabase } = await import("@/integrations/supabase/client");
    const { error } = await supabase
      .from("groepenkast_configurations")
      .delete()
      .eq("id", id);
    if (error) {
      toast.error("Verwijderen mislukt");
      return;
    }
    if (currentConfigId === id) setCurrentConfigId(null);
    await refreshSavedConfigs();
    toast.success("Configuratie verwijderd");
  };

  const newConfig = () => {
    setCurrentConfigId(null);
    setProjectNumber("");
    setCustomerName("");
    setProjectDate(new Date().toISOString().slice(0, 10));
    toast.info("Nieuwe configuratie gestart");
  };

  const printOrderPdf = () => {
    if (result.groupCount === 0) {
      toast.error("Voeg eerst minimaal 1 groep toe.");
      return;
    }
    const html = buildGroepenkastPdfHtml({
      projectNumber,
      customerName,
      projectDate,
      phases,
      mainAmps,
      includeSpd,
      reservePct,
      items: result.items.map((g) => ({
        label: g.label,
        type: g.type,
        amps: g.amps,
        qty: g.qty,
        modules: g.modules * g.qty,
      })),
      mainSwitchModules: result.mainSwitchModules,
      spdModules: result.spdModules,
      reserveModules: result.reserveModules,
      cabinet: result.cabinet,
      installedKw: result.installedKw,
      expectedKw: result.expectedKw,
      maxKw: result.maxKw,
      materialCost: result.materialCost,
      groupLabels: Object.entries(labels)
        .filter(([, v]) => v.trim())
        .map(([, v]) => v.trim()),
    });
    const w = typeof window !== "undefined" ? window.open("", "_blank") : null;
    if (!w) {
      toast.error("Pop-up geblokkeerd — sta pop-ups toe om de PDF te openen.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };


  if (!klantGeladen || !klant) {
    return (
      <>
        <Header />
        <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          {klantGeladen ? (
            <KlantGegevensPoort onGereed={setKlant} />
          ) : (
            <div className="mx-auto h-64 max-w-xl animate-pulse rounded-2xl bg-muted" />
          )}
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">

        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            AEG Groepenkast Configurator: stel je NEN 1010 kast samen
          </h1>
          <img
            src={aegLogo.url}
            alt="AEG"
            loading="lazy"
            decoding="async"
            className="hidden h-8 w-auto shrink-0 sm:block"
          />
        </div>
        <p className="mt-4 max-w-3xl text-base text-text-secondary sm:text-lg">
          Vink aan welke groepen je nodig hebt van licht en stopcontacten tot
          laadpaal, warmtepomp en zonnepanelen. De configurator bepaalt
          automatisch het <strong>aantal modules</strong>, de juiste{" "}
          <strong>kastbreedte</strong>, het type aardlekautomaten (A of B) en
          een complete <strong>stuklijst</strong> met{" "}
          <strong>AEG-componenten</strong>, volledig conform NEN 1010:2020.
        </p>

        {/* Intro quicknav */}
        <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: ListChecks,
              title: "Groepen kiezen",
              text: "Vink licht, stopcontacten, witgoed en zware verbruikers aan.",
            },
            {
              icon: Layers,
              title: "Modules & kast",
              text: "We bepalen het aantal DIN-modules en de juiste kastmaat.",
            },
            {
              icon: ShieldCheck,
              title: "ALB type A & B",
              text: "Automatisch type B voor laadpaal en omvormer (NEN 1010).",
            },
            {
              icon: Cpu,
              title: "Stuklijst",
              text: "Complete stuklijst met directe link naar onze shop.",
            },
          ].map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              className="rounded-xl border border-border bg-card p-5 transition-colors hover:border-orange/40"
            >
              <Icon className="size-6 text-orange" strokeWidth={2} />
              <h3 className="mt-3 text-sm font-bold text-foreground">{title}</h3>
              <p className="mt-1 text-xs leading-relaxed text-text-secondary">{text}</p>
            </div>
          ))}
        </section>

        <div className="mt-6 flex flex-wrap gap-2">
          {[
            { label: "Configurator", href: "#configurator" },
            { label: "Stuklijst", href: "#stuklijst" },
            { label: "Modules & regels", href: "#regels" },
            { label: "Veelgestelde vragen", href: "#faq" },
          ].map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
            >
              {item.label}
              <ChevronDown className="size-3.5" />
            </a>
          ))}
        </div>

        {/* Projectgegevens + opslaan in account */}
        <section className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-foreground">Projectgegevens</h2>
              <p className="text-xs text-text-secondary">
                Auto-save in deze browser
                {lastSavedAt && (
                  <> · laatst opgeslagen {lastSavedAt.toLocaleTimeString("nl-NL")}</>
                )}
                {currentConfigId && <> · gekoppeld aan account</>}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <GroepenkastBasisConfig onApply={applyBasisConfig} />
              {userId ? (
                <>
                  <button
                    type="button"
                    onClick={saveToAccount}
                    disabled={savingRemote}
                    className="inline-flex items-center gap-1.5 rounded-md bg-orange px-3 py-2 text-xs font-semibold text-white hover:bg-orange/90 disabled:opacity-60"
                  >
                    {savingRemote
                      ? "Opslaan…"
                      : currentConfigId
                        ? "Bijwerken in account"
                        : "Opslaan in account"}
                  </button>
                  {currentConfigId && (
                    <button
                      type="button"
                      onClick={newConfig}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:border-orange"
                    >
                      Nieuwe configuratie
                    </button>
                  )}
                </>
              ) : (
                <Link
                  to="/auth"
                  className="inline-flex items-center gap-1.5 rounded-md border border-orange/40 bg-orange/10 px-3 py-2 text-xs font-semibold text-orange hover:bg-orange/20"
                >
                  Inloggen om in account op te slaan
                </Link>
              )}
            </div>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div>
              <label className="text-xs font-semibold text-foreground">Projectnummer</label>
              <input
                type="text"
                value={projectNumber}
                onChange={(e) => setProjectNumber(e.target.value.slice(0, 50))}
                placeholder="bijv. P-2026-014"
                maxLength={50}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-orange focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground">Klantnaam</label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value.slice(0, 120))}
                placeholder="bijv. Bakkerij Jansen B.V."
                maxLength={120}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-orange focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground">Datum</label>
              <input
                type="date"
                value={projectDate}
                onChange={(e) => setProjectDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-orange focus:outline-none"
              />
            </div>
          </div>

          {userId && savedConfigs.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-semibold text-foreground">Opgeslagen in je account</p>
              <ul className="mt-2 divide-y divide-border rounded-md border border-border">
                {savedConfigs.map((c) => (
                  <li
                    key={c.id}
                    className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs ${
                      currentConfigId === c.id ? "bg-orange/5" : ""
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-foreground">
                        {c.project_number || "Zonder projectnummer"}
                        {c.customer_name && (
                          <span className="ml-2 font-normal text-text-secondary">
                            · {c.customer_name}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-text-secondary">
                        {new Date(c.project_date).toLocaleDateString("nl-NL")} ·
                        bijgewerkt {new Date(c.updated_at).toLocaleString("nl-NL")}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => loadFromAccount(c.id)}
                        className="rounded border border-border bg-background px-2 py-1 font-semibold hover:border-orange hover:text-orange"
                      >
                        Laden
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteFromAccount(c.id)}
                        className="rounded border border-border bg-background px-2 py-1 font-semibold text-[#3a7bd5] hover:border-[#3a7bd5]"
                      >
                        Verwijderen
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section
          id="configurator"
          className="mt-10 grid gap-6 lg:grid-cols-[1.1fr_1fr]"
        >

          {/* Inputs */}
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <div className="mb-6 flex items-center gap-2 text-orange">
              <Calculator className="size-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Configurator
              </span>
            </div>

            <div className="space-y-6">
              <div>
                <label className="text-sm font-semibold text-foreground">
                  Aansluiting
                </label>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {[1, 3].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPhases(p as 1 | 3)}
                      className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                        phases === p
                          ? "border-orange bg-orange text-white"
                          : "border-border bg-background text-foreground hover:border-orange"
                      }`}
                    >
                      {p === 1 ? "1-fase (230 V)" : "3-fase (400 V)"}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between text-sm font-semibold text-foreground">
                  Hoofdzekering
                  <span className="rounded-md bg-muted px-2 py-0.5 text-base font-bold text-foreground">
                    {phases}×{mainAmps} A
                  </span>
                </label>
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {[16, 25, 35, 40, 50, 63, 80, 100].map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setMainAmps(a)}
                      className={`rounded-md border px-2 py-2 text-xs font-semibold transition-colors ${
                        mainAmps === a
                          ? "border-orange bg-orange text-white"
                          : "border-border bg-background text-foreground hover:border-orange"
                      }`}
                    >
                      {a} A
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-foreground">
                    Groepen
                  </label>
                  <div className="flex items-center gap-2 text-xs">
                    <span
                      className={`font-bold ${
                        result.overload
                          ? "text-[#3a7bd5] dark:text-[#3a7bd5]"
                          : result.expectedKw / result.maxKw > 0.85
                            ? "text-orange"
                            : "text-emerald-600 dark:text-emerald-400"
                      }`}
                    >
                      {result.expectedKw.toFixed(1)} kW
                    </span>
                    <span className="text-text-secondary">
                      / {result.maxKw.toFixed(1)} kW
                    </span>
                  </div>
                </div>

                {/* Capaciteitsmeter */}
                <div className="mt-2">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        result.overload
                          ? "bg-[#3a7bd5]"
                          : result.expectedKw / result.maxKw > 0.85
                            ? "bg-orange"
                            : "bg-emerald-500"
                      }`}
                      style={{
                        width: `${Math.min(100, (result.expectedKw / result.maxKw) * 100)}%`,
                      }}
                    />
                  </div>
                  <p className="mt-1 text-right text-[11px] text-text-secondary">
                    {result.overload
                      ? `Overschreden met ${(result.expectedKw - result.maxKw).toFixed(1)} kW`
                      : `Nog ~${Math.max(0, Math.floor((result.maxKw - result.expectedKw) / 1.8))} groepen beschikbaar`}
                  </p>
                </div>
                <div className="mt-3 divide-y divide-border rounded-xl border border-border bg-background">
                  {groups.map((g) => (
                    <div
                      key={g.key}
                      className="flex items-center justify-between gap-3 p-3"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-foreground">
                          {g.label}
                          <span
                            className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                              g.type === "B"
                                ? "bg-orange/10 text-orange"
                                : "bg-muted text-text-secondary"
                            }`}
                          >
                            ALB {g.type}
                          </span>
                        </p>
                        <p className="text-xs text-text-secondary">
                          {g.hint} · {g.modules} mod · ~{g.kw} kW
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setCount(g.key, -1)}
                          className="size-8 rounded-md border border-border bg-background text-base font-bold text-foreground hover:border-orange hover:text-orange"
                          aria-label={`${g.label} min`}
                        >
                          −
                        </button>
                        <span className="w-7 text-center text-sm font-bold text-foreground">
                          {counts[g.key]}
                        </span>
                        <button
                          type="button"
                          onClick={() => setCount(g.key, +1)}
                          className="size-8 rounded-md border border-border bg-background text-base font-bold text-foreground hover:border-orange hover:text-orange"
                          aria-label={`${g.label} plus`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-foreground">
                    <input
                      type="checkbox"
                      checked={includeSpd}
                      onChange={(e) => setIncludeSpd(e.target.checked)}
                      className="size-4 accent-orange"
                    />
                    Overspanningsbeveiliging (SPD T2)
                  </label>
                  <p className="mt-1 text-[11px] text-text-secondary">
                    Verplicht in nieuwe installaties (NEN 1010:2020).
                  </p>
                </div>
                <div>
                  <label className="flex items-center justify-between text-xs font-semibold text-foreground">
                    Reserve
                    <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-bold text-foreground">
                      {reservePct}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={50}
                    step={5}
                    value={reservePct}
                    onChange={(e) => setReservePct(Number(e.target.value))}
                    className="mt-2 w-full accent-orange"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="rounded-2xl border border-orange/30 bg-gradient-to-br from-orange/5 to-transparent p-6 shadow-sm sm:p-8">
            <div className="mb-4 flex items-center gap-2 text-orange">
              <Zap className="size-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Resultaat
              </span>
            </div>

            <p className="text-sm text-text-secondary">Aanbevolen kast(en)</p>
            <p className="mt-1 text-4xl font-bold tracking-tight text-foreground">
              {result.cabinet / 24} × 24 modules
            </p>
            <p className="mt-1 text-xs text-text-secondary">
              {result.usedModules} gebruikt + {result.reserveModules} reserve
              ({reservePct}%) · {result.groupCount} groepen · elke kast 2×12 modules
            </p>

            {result.overload && (
              <div className="mt-4 flex gap-2 rounded-md border border-[#3a7bd5]/40 bg-[#3a7bd5]/5 p-3 text-xs text-[#3a7bd5] dark:text-[#3a7bd5]">
                <AlertTriangle className="size-4 shrink-0" />
                <span>
                  Het verwachte gelijktijdige vermogen (
                  {result.expectedKw.toFixed(1)} kW) overschrijdt je
                  hoofdzekering ({result.maxKw.toFixed(1)} kW). Overweeg
                  verzwaring of dynamische load balancing.
                </span>
              </div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                  Modules ALB type A
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  {result.albAModules}
                </p>
                <p className="text-[10px] text-text-secondary">licht & stopc.</p>
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                  Modules ALB type B
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  {result.albBModules}
                </p>
                <p className="text-[10px] text-text-secondary">EV / omvormer</p>
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                  Geïnstalleerd
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  {result.installedKw.toFixed(1)} kW
                </p>
                <p className="text-[10px] text-text-secondary">som groepen</p>
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                  Verwacht gelijktijdig
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  {result.expectedKw.toFixed(1)} kW
                </p>
                <p className="text-[10px] text-text-secondary">
                  diversiteit 0,5
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/"
                hash="contact"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
              >
                Offerte opvragen
              </Link>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 rounded-md border border-orange/40 bg-orange/5 px-4 py-2 text-sm font-semibold text-orange hover:bg-orange/10"
                aria-label="Deel deze configuratie via link"
              >
                {copied ? <Check className="size-4" /> : <Share2 className="size-4" />}
                {copied ? "Link gekopieerd" : "Deel configuratie"}
              </button>
            </div>
            <p className="mt-2 text-[11px] text-text-secondary">
              Je instellingen worden automatisch opgeslagen in de URL — bookmark
              of deel deze pagina om er direct naar terug te keren.
            </p>
          </div>
        </section>

        {/* Visuele weergave van de kast */}
        <section id="kastweergave" className="mt-12">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold tracking-tight">
              Weergave van je AEG groepenkast
            </h2>
            <img
              src={aegLogo.url}
              alt="AEG"
              loading="lazy"
              decoding="async"
              className="h-6 w-auto"
            />
          </div>
          <p className="mt-2 max-w-3xl text-sm text-text-secondary">
            Zo komt je kast eruit te zien: elke kast is 2 rails × 12 modules
            (24 modules). Bij meer dan 24 modules komt er een tweede kast bij.
            Tik of klik op een groep om er een eigen naam aan te geven — die naam
            nemen we mee in je offerte.
          </p>

          <div className="mt-5 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  AEG groepenkast
                </span>
                <img
                  src={aegLogo.url}
                  alt="AEG"
                  loading="lazy"
                  decoding="async"
                  className="h-5 w-auto"
                />
              </div>
              <GroepenkastCanvas
                blocks={moduleBlocks}
                selectedId={selectedBlockId}
                onSelect={(id) => setSelectedBlockId(id)}
              />
              <p className="mt-3 text-xs text-text-secondary">
                {result.usedModules} modules gebruikt ·{" "}
                {result.reserveModules} modules reserve ·{" "}
                {result.cabinet / 24} kast(en) van 2×12 modules
              </p>
            </div>

            <div
              ref={labelPanelRef}
              className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6"
            >
              <h3 className="text-sm font-bold text-foreground">
                Groep labelen
              </h3>
              {selectedBlock ? (
                <div className="mt-3">
                  <p className="text-xs text-text-secondary">
                    {selectedBlock.title}
                  </p>
                  <input
                    ref={labelInputRef}
                    type="text"
                    maxLength={24}
                    inputMode="text"
                    value={labels[selectedBlock.id] ?? ""}
                    onChange={(e) =>
                      setLabels((prev) => ({
                        ...prev,
                        [selectedBlock.id]: e.target.value.slice(0, 24),
                      }))
                    }
                    placeholder="bijv. Woonkamer"
                    className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-orange focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setSelectedBlockId(null)}
                    className="mt-2 text-xs font-semibold text-orange hover:underline"
                  >
                    Klaar
                  </button>
                </div>
              ) : (
                <p className="mt-2 text-xs text-text-secondary">
                  Klik in de tekening op een groep om die een naam te geven.
                </p>
              )}

              <div className="mt-5 border-t border-border pt-4">
                <h3 className="text-sm font-bold text-foreground">Legenda</h3>
                <ul className="mt-2 space-y-1 text-xs text-text-secondary">
                  {result.items.map((item) => (
                    <li key={item.key} className="flex justify-between gap-3">
                      <span>{item.label}</span>
                      <span className="font-semibold text-foreground">
                        {item.qty}× · {item.modules * item.qty} mod
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="button"
                onClick={requestQuote}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-orange px-4 py-2.5 text-sm font-semibold text-orange-foreground hover:bg-orange-hover"
              >
                Offerte aanvragen op deze configuratie
                <ArrowRight className="size-4" />
              </button>
              <p className="mt-2 text-[11px] text-text-secondary">
                We sturen je configuratie mee, zodat we een passende offerte voor
                je kunnen opstellen.
              </p>
            </div>
          </div>
        </section>



        {/* Bill of materials */}
        <section id="stuklijst" className="mt-12">
          <h2 className="text-xl font-bold tracking-tight">
            AEG stuklijst op basis van jouw configuratie
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Indicatieve AEG-onderdelen volgens NEN 1010:2020 — definitieve keuze
            (merk, karakteristiek, kortsluitvastheid) maakt de installateur op
            basis van de installatie.
          </p>
          <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full min-w-[420px] text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-text-secondary">
                <tr>
                  <th className="px-4 py-3">Onderdeel</th>
                  <th className="px-4 py-3">Aantal</th>
                  <th className="px-4 py-3">Modules</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="px-4 py-3 font-semibold text-foreground">
                    Hoofdschakelaar {phases === 1 ? "2P" : "4P"} {mainAmps} A
                  </td>
                  <td className="px-4 py-3">1</td>
                  <td className="px-4 py-3">{result.mainSwitchModules}</td>
                </tr>
                {includeSpd && (
                  <tr>
                    <td className="px-4 py-3 font-semibold text-foreground">
                      Overspanningsbeveiliging type 2 ({phases === 1 ? "1+N" : "3+N"})
                    </td>
                    <td className="px-4 py-3">1</td>
                    <td className="px-4 py-3">{result.spdModules}</td>
                  </tr>
                )}
                {result.items.map((g) => (
                  <tr key={g.key}>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-foreground">{g.label}</span>
                      <span
                        className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                          g.type === "B"
                            ? "bg-orange/10 text-orange"
                            : "bg-muted text-text-secondary"
                        }`}
                      >
                        ALB {g.type} · {g.amps} A
                      </span>
                    </td>
                    <td className="px-4 py-3">{g.qty}</td>
                    <td className="px-4 py-3">{g.modules * g.qty}</td>
                  </tr>
                ))}
                <tr className="bg-orange/5">
                  <td className="px-4 py-3 font-bold text-foreground">
                    Reserve ({reservePct}%) + behuizing {result.cabinet} mod
                  </td>
                  <td className="px-4 py-3">—</td>
                  <td className="px-4 py-3 font-bold text-foreground">
                    {result.reserveModules}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* AEG-producten uit ons assortiment */}
        <section id="aeg-producten" className="mt-12">
          <h2 className="text-xl font-bold tracking-tight">
            AEG-producten die wij gebruiken
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Actueel AEG-assortiment uit ons eigen systeem. Prijzen ontvang je in
            de offerte.
          </p>
          {aegQuery.isLoading && (
            <p className="mt-4 text-sm text-text-secondary">
              Producten worden opgehaald…
            </p>
          )}
          {aegQuery.isError && (
            <p className="mt-4 text-sm text-text-secondary">
              De productenlijst is nu even niet beschikbaar.
            </p>
          )}
          {aegArtikelen.length > 0 && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {aegArtikelen.map((a) => (
                <div
                  key={a.artikelnummer}
                  className="flex gap-3 rounded-xl border border-border bg-card p-4"
                >
                  {a.afbeelding_url && (
                    <img
                      src={a.afbeelding_url}
                      alt={a.naam}
                      loading="lazy"
                      className="h-16 w-16 shrink-0 rounded-lg bg-muted object-contain"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">
                      {a.naam}
                    </p>
                    <p className="mt-1 text-xs text-text-secondary">
                      {[
                        a.categorie,
                        a.modules ? `${a.modules} modules` : null,
                        a.polen,
                        a.ampere ? `${a.ampere} A` : null,
                        a.differentieel_ma ? `${a.differentieel_ma} mA` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>


        {/* Rules */}
        <section id="regels" className="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              Modulebreedtes (DIN-rail, 18 mm)
            </h2>
            <ul className="mt-3 space-y-3 text-sm text-text-secondary">
              <li>
                <strong className="text-foreground">Hoofdschakelaar:</strong>{" "}
                2 modules (1-fase) of 4 modules (3-fase)
              </li>
              <li>
                <strong className="text-foreground">SPD type 2:</strong> 2
                modules (1+N) of 4 modules (3+N)
              </li>
              <li>
                <strong className="text-foreground">Groepen:</strong>{" "}
                1 module per 1-fase groep; kookgroep 1-fase 2 modules; 3-fase groepen (kookgroep, laadpaal) 4 modules
              </li>
              <li>
                <strong className="text-foreground">Aardlekschakelaar:</strong>{" "}
                2 modules (1-fase) of 4 modules (3-fase) per schakelaar
              </li>
              <li>
                <strong className="text-foreground">Kast:</strong> 2 rails × 12
                modules = 24 modules; bij meer dan 24 modules komt er een extra kast bij
              </li>
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              NEN 1010 vereisten
            </h2>
            <ul className="mt-3 space-y-2">
              {[
                "Eén aardlekautomaat (ALB) per groep — combinatie van installatieautomaat + aardlekschakelaar.",
                "Overspanningsbeveiliging type 2 verplicht in nieuwe installaties (clausule 443).",
                "Laadpalen vereisen ALB type B voor detectie van DC-lekstroom.",
                "Natte ruimtes (badkamer, buiten) altijd minimaal type A, voorkeur 30 mA.",
                "Reserveer minimaal 20% modules voor toekomstige uitbreiding (laadpaal, batterij).",
                "Hoofdschakelaar moet alle stroom in één handeling kunnen onderbreken.",
              ].map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight">
            Veelgestelde vragen over de groepenkast configurator
          </h2>
          <div className="mt-4 divide-y divide-border rounded-xl border border-border bg-card">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={f.q} className="p-5">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="flex w-full items-center justify-between text-left font-semibold text-foreground"
                  >
                    {f.q}
                    <span
                      className={`ml-3 text-orange transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </button>
                  {isOpen && <p className="mt-2 text-text-secondary">{f.a}</p>}
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-12 rounded-2xl bg-card p-6 ring-1 ring-border sm:p-8">
          <h2 className="text-xl font-bold tracking-tight">
            Hulp nodig bij de definitieve keuze?
          </h2>
          <p className="mt-2 text-text-secondary">
            Onze erkende installateurs adviseren over kortsluitvastheid (kA),
            karakteristiek, AEG-componenten en bekabeling, en
            plaatsen de kast volgens NEN 1010 inclusief eventuele
            verzwaringsaanvraag bij de netbeheerder.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/c/$category/$subcategory"
              params={{ category: "elektro", subcategory: "groepenkasten" }}
              className="inline-flex items-center rounded-md bg-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange/90"
            >
              Bekijk groepenkasten
            </Link>
            <Link
              to="/"
              hash="contact"
              className="inline-flex items-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
            >
              Neem contact op
            </Link>
          </div>
        </section>

        <section className="mt-12 rounded-2xl bg-card p-6 ring-1 ring-border sm:p-8">
          <h2 className="text-xl font-bold tracking-tight">
            Groepenkast vervangen in uw stad
          </h2>
          <p className="mt-2 text-text-secondary">
            Heeft u al een configuratie samengesteld? Onze installateurs komen ook bij u langs. Bekijk onze lokale groepenkast-pagina's.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {[
              { label: "Rotterdam", slug: "rotterdam" },
              { label: "Utrecht", slug: "utrecht" },
              { label: "Tilburg", slug: "tilburg" },
              { label: "Nijmegen", slug: "nijmegen" },
            ].map((city) => (
              <Link
                key={city.slug}
                to="/groepenkast-vervangen/$stad"
                params={{ stad: city.slug }}
                className="inline-flex items-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
              >
                {city.label}
              </Link>
            ))}
          </div>
        </section>
      </main>
      <AlertDialog
        open={pendingAdd !== null}
        onOpenChange={(o) => !o && setPendingAdd(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hoofdzekering overschreden</AlertDialogTitle>
            <AlertDialogDescription>
              Met deze groep komt het verwachte gelijktijdige vermogen op{" "}
              <strong>{pendingAdd?.newKw.toFixed(1)} kW</strong>, meer dan je
              hoofdzekering van {phases}×{mainAmps} A aankan (max ca.{" "}
              <strong>{pendingAdd?.maxKw.toFixed(1)} kW</strong>). Dit kan
              leiden tot het uitvallen van de hoofdzekering. Overweeg een
              zwaardere aansluiting of dynamische load balancing. Wil je toch
              doorgaan?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingAdd(null)}>
              Annuleren
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingAdd) {
                  setOverrideAllowed(true);
                  applyDelta(pendingAdd.key, 1);
                  setPendingAdd(null);
                }
              }}
            >
              Toch toevoegen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Mobiele actiebalk: configuratie altijd binnen duimbereik */}
      <div className="h-20 lg:hidden" aria-hidden="true" />
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur lg:hidden">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-foreground">
              {phases}×{mainAmps} A · {result.usedModules}/{result.cabinet} modules
              · {result.cabinet / 24} kast(en)
            </p>
            <p className="truncate text-[11px] text-text-secondary">
              {result.items.reduce((n, i) => n + i.qty, 0)} groepen gekozen
            </p>
          </div>
          <button
            type="button"
            onClick={requestQuote}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-orange px-4 py-3 text-sm font-semibold text-orange-foreground hover:bg-orange-hover"
          >
            Offerte aanvragen
            <ArrowRight className="size-4" />
          </button>
        </div>
      </div>

      <Footer />
    </>
  );
}
